import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiRefreshCw, FiCheck } from "react-icons/fi";
import { authFetch } from "../api";
import { StatTile, Refreshing } from "./dashboard/charts";
import { ENTITY, greeting, timeAgo } from "./dashboard/chartUtils";
import { AISection, ActivityPanel, AttentionPanel, BlogsSection, ChatbotsSection, CoWorkSection, FoundationSection, HealthPanel, InboxSection, OverviewChart, WebsiteSection } from "./dashboard/Sections";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];
const JUMPS = [["section-ai", "AI tools", ENTITY.ai], ["section-chatbots", "Chatbots", ENTITY.chat], ["section-cowork", "Co-Work", ENTITY.cowork], ["section-foundation", "Foundation", ENTITY.foundation], ["section-blogs", "Blogs", ENTITY.blogs], ["section-inbox", "Enquiries", ENTITY.inbox]];
const KPI_COLORS = { ai: ENTITY.ai, chat: ENTITY.chat, leads: ENTITY.chat, inbox: ENTITY.inbox, cowork: ENTITY.cowork, foundation: ENTITY.foundation, blogs: ENTITY.blogs };

function Skeleton() {
  return (
    <div className="animate-pulse space-y-6" aria-label="Loading the dashboard" role="status">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }, (_, i) => <div key={i} className="h-36 rounded-2xl bg-navy border border-navy-border" />)}</div>
      <div className="grid xl:grid-cols-3 gap-6"><div className="xl:col-span-2 h-80 rounded-2xl bg-navy border border-navy-border" /><div className="h-80 rounded-2xl bg-navy border border-navy-border" /></div>
      <div className="h-96 rounded-2xl bg-navy border border-navy-border" />
    </div>
  );
}

// The dashboard: the numbers that matter at the top, everything over time, what needs a person, then a section for each part of the site with its own charts.
export default function AdminDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [now, setNow] = useState(Date.now());
  const alive = useRef(true);

  const load = useCallback(async (range) => {
    setBusy(true);
    try {
      const res = await authFetch(`/core/dashboard/?days=${range}`);
      if (!res.ok) throw new Error("bad");
      const json = await res.json();
      if (alive.current) { setData(json); setError(""); setNow(Date.now()); }
    } catch {
      if (alive.current) setError("The dashboard could not be loaded.");
    } finally {
      if (alive.current) setBusy(false);
    }
  }, []);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { load(days); const t = setInterval(() => load(days), 120000); return () => clearInterval(t); }, [days, load]);
  useEffect(() => { authFetch("/core/profile/").then((r) => r.json()).then((p) => setName(String(p.first_name || p.username || p.email || "").split("@")[0])).catch(() => {}); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);

  const waiting = data ? data.attention.filter((a) => a.tone !== "info").reduce((t, a) => t + a.count, 0) : 0;
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">{greeting(new Date().getHours())}{name ? `, ${name}` : ""}</h1>
          <p className="text-sm text-gray-400 mt-1">{today} - here is how everything is going.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div role="group" aria-label="Period" className="inline-flex rounded-xl bg-navy border border-navy-border p-1">
            {RANGES.map(([value, label]) => (
              <button key={value} type="button" onClick={() => setDays(value)} aria-pressed={days === value}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${days === value ? "bg-brand-gradient text-white" : "text-gray-300 hover:bg-white/5"}`}>{days === value && <FiCheck aria-hidden="true" size={14} />}{label}</button>
            ))}
          </div>
          <button type="button" onClick={() => load(days)} disabled={busy} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-navy border border-navy-border text-sm text-gray-200 hover:border-white/30 disabled:opacity-60" aria-label="Refresh the dashboard">
            <FiRefreshCw aria-hidden="true" className={busy ? "animate-spin" : ""} /> <span className="hidden sm:inline">{data ? `Updated ${timeAgo(data.generated_at, now)}` : "Refresh"}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm flex items-center justify-between gap-3" role="alert">
          <span className="text-red-100">{error}</span><button type="button" onClick={() => load(days)} className="underline text-red-100">Try again</button>
        </div>
      )}

      {!data && !error && <Skeleton />}
      {!data && error && <Skeleton />}

      {data && (
        <Refreshing busy={busy}>
          <nav aria-label="Jump to a section" className="flex flex-wrap gap-2 mb-6">
            {JUMPS.map(([id, label, color]) => (
              <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-navy border border-navy-border text-xs text-gray-300 hover:text-white hover:border-white/30"><span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: color }} />{label}</a>
            ))}
          </nav>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {data.kpis.map((k) => <StatTile key={k.key} label={k.label} value={k.value} delta={k.delta} hint={k.hint} series={k.series} color={KPI_COLORS[k.key]} to={k.to} />)}
            <StatTile label="Waiting for you" value={waiting} hint={waiting ? `${data.attention.length} kinds of things to do` : "All clear"} color="#898781" to={data.attention[0] ? data.attention[0].to : undefined} />
          </div>

          <div className="grid xl:grid-cols-3 gap-6 mb-6">
            <div className="xl:col-span-2"><OverviewChart data={data} /></div>
            <AttentionPanel items={data.attention} />
          </div>

          <div className="grid xl:grid-cols-3 gap-6 mb-6">
            <div className="xl:col-span-2"><ActivityPanel items={data.activity} /></div>
            <HealthPanel items={data.health} />
          </div>

          <div className="space-y-6">
            <AISection data={data} />
            <ChatbotsSection data={data} />
            <CoWorkSection data={data} />
            <FoundationSection data={data} />
            <BlogsSection data={data} />
            <div className="grid xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2"><InboxSection data={data} /></div>
              <WebsiteSection data={data} />
            </div>
          </div>
        </Refreshing>
      )}
    </div>
  );
}
